/**
 * GenerateLiveStreamTokenUseCase
 *
 * Domain entry point that fetches a fresh Agora live-stream token and returns a
 * `LiveStreamAuthEntity` (or `null` on failure). Bridges the data-layer Cloud
 * Function call to the page via `VideoCallService`.
 *
 * Flutter source: lib/presentation/screens/chat/video_call_screen.dart:523-530
 *   (`_generateLiveStreamingToken`) and :124-135 (`_init` uses the result; a null
 *   result or a null `token` aborts the call).
 *
 *   - `channel` = `widget.groupChatId` (`chatUsersInfo.groupChatId`).
 *   - `uid` = `_liveStreamingUserId` (the current local streaming id, 0 until joined).
 *   - `role` = `ClientRoleType.clientRoleBroadcaster.name`, supplied by the caller
 *     from the `VideoCallRole.broadcaster` enum value (Task 5).
 *
 * Returns `null` when the service returns `null` — the failure signal the caller
 * (the Task-10 `useVideoCall` hook) checks before joining the channel, matching
 * `video_call_screen.dart:127`.
 *
 * Pure-domain contract: the use case forwards `channel` / `uid` / `role`
 * unchanged and does not dispatch to Redux — the Agora/token lifecycle is a
 * presentation concern.
 */

import { VideoCallService, videoCallService } from '@/domain/services/videoCall/videoCallService';
import type { LiveStreamAuthEntity } from '@/domain/entities/liveStreamAuth/liveStreamAuthEntity';

export interface GenerateLiveStreamTokenParams {
  /** The Agora channel id (`chatUsersInfo.groupChatId`). */
  channel: string;
  /** The local streaming id (`_liveStreamingUserId`, 0 until joined). */
  uid: number;
  /**
   * The Agora client role wire string — `VideoCallRole.broadcaster`
   * (= `ClientRoleType.clientRoleBroadcaster.name`).
   */
  role: string;
}

export class GenerateLiveStreamTokenUseCase {
  private videoCallService: VideoCallService;

  constructor(service: VideoCallService = videoCallService) {
    this.videoCallService = service;
  }

  /**
   * Fetch a fresh Agora token for `channel` / `uid` / `role`.
   *
   * @returns The `LiveStreamAuthEntity` on success, or `null` when the service
   *   returns `null` (parity with `video_call_screen.dart:127`).
   */
  async execute({ channel, uid, role }: GenerateLiveStreamTokenParams): Promise<LiveStreamAuthEntity | null> {
    return this.videoCallService.generateLiveStreamToken(channel, uid, role);
  }
}

// Export singleton instance
export const generateLiveStreamTokenUseCase = new GenerateLiveStreamTokenUseCase();
